import {VisuallyHidden} from '@commercial-helios/ui/src/components/visually-hidden'
import {colors, headerSizes, tokens} from '@commercial-helios/ui/theme'
import {matchSorter} from 'match-sorter'
import React from 'react'
import styled from 'styled-components'

import type {JobListingQuery} from '@/graphql-types'
import type {GreenhouseListingType} from '@/utils/get-jobs-by-department'
import {getJobsByDepartment} from '@/utils/get-jobs-by-department'
import Input from '@helios/shared/components/input'
import LocalizedLink from '@helios/shared/components/localized-link'
import Select from '@helios/shared/components/select'
import {text, Text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'
import {TrackingEvent, track} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'
import replacePlaceholders from '@helios/shared/utils/replace-placeholders'

interface Props {
    allJobs: GreenhouseListingType
    headings: JobListingQuery['prismicJobListing']['data']['listing_table_headings'][number]
}

const ALL_DEPARTMENTS_VALUE = 'all'

const useGetFilteredJobs = (
    allJobs: GreenhouseListingType,
    searchTerm: string,
    selectValue: string
) => {
    return React.useMemo(() => {
        let results = getJobsByDepartment(allJobs)

        if (searchTerm.trim() !== '') {
            results = getJobsByDepartment(
                matchSorter(allJobs, searchTerm, {
                    keys: [{threshold: matchSorter.rankings.CONTAINS, key: '*.title'}],
                })
            )
        }

        if (selectValue !== ALL_DEPARTMENTS_VALUE) {
            if (!results[selectValue]) {
                return {}
            }

            results = {[selectValue]: results[selectValue]}
        }

        return results
    }, [allJobs, searchTerm, selectValue])
}

export const JobListingWithFilters = ({allJobs, headings}: Props) => {
    const [selectValue, setSelectValue] = React.useState(ALL_DEPARTMENTS_VALUE)
    const [searchTerm, setSearchTerm] = React.useState('')
    const results = useGetFilteredJobs(allJobs, searchTerm, selectValue)

    const departments = Object.keys(getJobsByDepartment(allJobs)).map((department) => ({
        label: department,
        value: department,
    }))
    departments.unshift({
        label: headings.all_departments_label,
        value: ALL_DEPARTMENTS_VALUE,
    })

    const clearFilters = () => {
        setSearchTerm('')
        setSelectValue(ALL_DEPARTMENTS_VALUE)
    }

    const handleFilterChange = (value: string) => {
        track(TrackingEvent.CareersPageTeamSearchActioned, {action: 'completed'})
        setSelectValue(value)
    }

    const getSearchResultsString = () => {
        if (searchTerm !== '') {
            const keywordSearchString = replacePlaceholders(
                headings.showing_roles_by_department_with_search,
                {
                    '{{term}}': searchTerm,
                }
            ).join('')
            const filterString = selectValue !== ALL_DEPARTMENTS_VALUE ? `in ${selectValue}` : ''

            return `${keywordSearchString} ${filterString}`
        }

        return selectValue === ALL_DEPARTMENTS_VALUE
            ? headings.no_search_label
            : replacePlaceholders(headings.showing_roles_by_department, {
                  '{{department}}': selectValue,
              })
    }

    return (
        <SectionWrapper>
            <FiltersWrapper>
                <Input
                    placeholder={headings.search_input_placeholder}
                    value={searchTerm}
                    data-testid="jobs-filter-search"
                    onChange={setSearchTerm}
                    // Trigger tracking on focus so event isn't sent on every keystroke
                    onFocus={() =>
                        track(TrackingEvent.CareersPageKeywordSearchActioned, {action: 'completed'})
                    }
                />
                <Select
                    options={departments}
                    value={selectValue}
                    data-testid="jobs-filter-dropdown"
                    placeholder={'Select'}
                    onChange={handleFilterChange}
                />
            </FiltersWrapper>

            <ResultsWrapper aria-live="polite" data-testid="jobs-search-results">
                <SearchResultsStringWrapper>
                    <Text
                        variant="paragraph-base"
                        color="lightMuted"
                        data-testid="applied-filters-label"
                    >
                        {getSearchResultsString()}
                    </Text>
                    {(searchTerm || selectValue !== ALL_DEPARTMENTS_VALUE) && (
                        <ClearFiltersButton onClick={clearFilters} data-testid="clear-filters">
                            {headings.clear_filters} ✕
                        </ClearFiltersButton>
                    )}
                </SearchResultsStringWrapper>

                {Object.keys(results).length > 0 ? (
                    <StyledTable>
                        <thead>
                            <tr>
                                <Text as="th" scope="col" variant="paragraph-base">
                                    {headings.team}
                                </Text>
                                <Text as="th" colSpan={2} scope="col" variant="paragraph-base">
                                    <FakeCols>
                                        <RoleLabel>{headings.role}</RoleLabel>
                                        <VisuallyHidden> and </VisuallyHidden>
                                        <span>{headings.location}</span>
                                    </FakeCols>
                                </Text>
                            </tr>
                        </thead>

                        {Object.keys(results).map((department, departmentIndex) => {
                            const roles = results[department]
                            if (!roles) {
                                return null
                            }

                            return (
                                <tbody key={department}>
                                    <tr>
                                        <th colSpan={3}>
                                            <VisuallyHidden>{department}</VisuallyHidden>
                                        </th>
                                    </tr>
                                    {roles.map((role, i) => (
                                        <tr key={role.title}>
                                            <DepartmentTitleTH scope="row">
                                                <Text
                                                    as="span"
                                                    variant="title-base"
                                                    data-testid="jobs-search-result-heading"
                                                >
                                                    {department}
                                                </Text>
                                            </DepartmentTitleTH>
                                            <td colSpan={2}>
                                                <JobPill
                                                    to={`/careers/jobs/${role.gh_Id}`}
                                                    data-testid={`jobs-search-result-item-${departmentIndex}-${i}`}
                                                >
                                                    <FakeCols>
                                                        <Text as="span" variant="paragraph-large">
                                                            {role.title}
                                                        </Text>
                                                        <VisuallyHidden>
                                                            at location:
                                                        </VisuallyHidden>
                                                        <Text
                                                            as="span"
                                                            variant="paragraph-base"
                                                            color="lightMuted"
                                                        >
                                                            {role.offices
                                                                .map((office) => office.name)
                                                                .join(', ')}
                                                        </Text>
                                                    </FakeCols>
                                                </JobPill>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            )
                        })}
                    </StyledTable>
                ) : (
                    <Text variant="title-base">{headings.no_jobs_found}</Text>
                )}
            </ResultsWrapper>
        </SectionWrapper>
    )
}

const MOBILE_BREAKPOINT = 'sm'

const SectionWrapper = styled.section`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min(MOBILE_BREAKPOINT)} {
        gap: 80px;
    }
`

const ResultsWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing40};

    ${mq.min(MOBILE_BREAKPOINT)} {
        gap: ${tokens.spacing12};
    }
`

const FiltersWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min(MOBILE_BREAKPOINT)} {
        grid-auto-flow: column;
        grid-template-columns: 1fr 1fr;
    }
`

const SearchResultsStringWrapper = styled.div`
    display: grid;
    grid-auto-flow: row;
    gap: ${tokens.spacing8};

    ${mq.min(MOBILE_BREAKPOINT)} {
        gap: ${tokens.spacing14};
        grid-auto-flow: column;
        justify-content: left;
    }
`

const StyledTable = styled.table`
    width: 100%;
    table-layout: fixed;
    border-spacing: 0;

    th {
        text-align: left;
    }

    thead th {
        position: sticky;
        top: ${headerSizes.heightSmall}px;
        vertical-align: top;
        background-color: ${colors.background.default};

        ${mq.max(MOBILE_BREAKPOINT)} {
            display: none;
        }

        ${mq.min(MOBILE_BREAKPOINT)} {
            padding: 80px 0 ${tokens.spacing12};
        }

        border-bottom: 1px solid ${colors.border.default};
    }

    tbody td {
        padding: ${tokens.spacing10} 0;
        vertical-align: top;

        ${mq.max(MOBILE_BREAKPOINT)} {
            display: table-row;
        }
    }

    tbody tr:nth-child(2) th {
        ${mq.max(MOBILE_BREAKPOINT)} {
            padding: ${tokens.spacing10} 0;
        }
    }

    tbody tr:nth-child(2) > td,
    tbody tr:nth-child(2) > th {
        ${mq.min(MOBILE_BREAKPOINT)} {
            padding-top: ${tokens.spacing40};
        }
    }

    tbody tr:nth-child(2) > th {
        ${mq.max(MOBILE_BREAKPOINT)} {
            padding-top: ${tokens.spacing40};
        }
    }

    tbody tr:last-child > td {
        padding-bottom: ${tokens.spacing40};
    }

    tbody tr:nth-child(n + 3) th > * {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
        clip-path: inset(50%);
    }

    tr {
        ${mq.max(MOBILE_BREAKPOINT)} {
            display: grid;
        }
    }

    thead + tbody tr:first-child th {
        ${mq.max(MOBILE_BREAKPOINT)} {
            display: none;
        }
    }

    tbody:not(:first-of-type) tr:first-child th {
        border-bottom: 1px solid ${colors.border.default};
    }

    tbody:last-child tr:last-child > td {
        padding-bottom: 0;
    }
`

const ClearFiltersButton = styled.button`
    all: unset;
    cursor: pointer;
    ${text({color: 'brand', variant: 'paragraph-base'})}

    :focus {
        ${focusStyles()}
    }
`

const DepartmentTitleTH = styled.th`
    ${mq.min(MOBILE_BREAKPOINT)} {
        display: grid;
    }
`

const FakeCols = styled.span`
    display: grid;
    grid-gap: ${tokens.spacing10};

    ${mq.min(MOBILE_BREAKPOINT)} {
        grid-template-columns: 1fr 1fr;
    }
`

const RoleLabel = styled.span`
    ${mq.min(MOBILE_BREAKPOINT)} {
        padding-left: ${tokens.spacing20};
    }
`

const JobPill = styled(LocalizedLink)`
    display: block;
    padding: ${tokens.spacing20};
    cursor: pointer;
    background-color: ${colors.background.muted};
    border: 1px solid transparent;
    border-radius: ${tokens.arc8};

    :hover,
    :active {
        border-color: ${colors.border.secondary};
    }
`
